/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import {
    IGroup,
    IGroupDelivery,
    IGroups,
    IHelp,
    IOrderDetailsViewProps,
    IOrderInformation,
    IOrderSummary,
    IPaymentMethods,
    ISalesLine
} from '@msdyn365-commerce-modules/order-management';
import { IImageSettings, Image, IRequestContext, IActionContext, ICultureInfoFormatter } from '@msdyn365-commerce/core';
import { getFallbackImageUrl } from '@msdyn365-commerce-modules/retail-actions';
import { INodeProps, Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import {
    IOrderDetailsProps,
    IOrderDetailsResources,
    IOrderDetailsConfig
} from '../definition-extensions/order-details.ext.props.autogenerated';
import { IOrderDetailsExtData } from '../definition-extensions/order-details.data';
import { getSalesOrderDetailsAsync } from '../../../actions/DataActionExtension.g';
import { IContainerDetailsEntity } from '../../../actions/DataServiceEntities.g';
import {
    IShipmentTrackingProps,
    IShipmentTrackingResources,
    ShipmentTrackingComponent
} from './components/order-details.shipment-tracking.component';
import {
    INoShipmentTrackingProps,
    INoShipmentTrackingResources,
    NoShipmentTrackingComponent
} from './components/order-details.no-shipment-tracking.component';
import { IOrderDetailsContainerRowProps, OrderDetailsContainerRowComponent } from './components/order-details.container-row.component';
import { SimpleProduct } from '@msdyn365-commerce/retail-proxy';

interface IExtendedOrderInformation extends IOrderInformation {
    purchaseOrderNumber: React.ReactNode;
}

interface IGroupsExtended extends IGroups {
    actionContext: IActionContext;
    cultureFormatter: ICultureInfoFormatter;
    request: IRequestContext;
    containerDetails: IContainerDetailsEntity[];
    resources: IOrderDetailsResources;
    config: IOrderDetailsConfig;
}

interface IGroupExtended extends IGroup {
    actionContext: IActionContext;
    cultureFormatter: ICultureInfoFormatter;
    request: IRequestContext;
    containerDetails: IContainerDetailsEntity[];
    resources: IOrderDetailsResources;
    config: IOrderDetailsConfig;
}

interface IGroupedSalesLine {
    actionContext: IActionContext;
    cultureFormatter: ICultureInfoFormatter;
    request: IRequestContext;
    salesLines: ISalesLine[];
    containerDetails: IContainerDetailsEntity[];
    resources: IOrderDetailsResources;
    config: IOrderDetailsConfig;
}

interface IOrderDetailsContainerImageProps {
    product: SimpleProduct | undefined;
    actionContext: IActionContext;
    requestContext: IRequestContext;
    imageSettings: IImageSettings | undefined;
}

export const OrderInfomation: React.FC<IExtendedOrderInformation> = ({
    orderInformationProps,
    salesId,
    receiptId,
    createdDate,
    count,
    amount,
    channelName,
    channelAddress,
    purchaseOrderNumber
}) => (
    <Node {...orderInformationProps}>
        {channelName}
        {channelAddress}
        {salesId}
        {purchaseOrderNumber}
        {receiptId}
        {createdDate}
        {count}
        {amount}
    </Node>
);

export const SalesLine: React.FC<ISalesLine> = ({ salesLineProps, salesLine, buyAgainButton, errors }) => (
    <Node {...salesLineProps}>
        {salesLine}
        {buyAgainButton}
        {errors}
    </Node>
);

const GroupedSalesLine: React.FC<IGroupedSalesLine> = ({
    containerDetails,
    salesLines,
    resources,
    request,
    actionContext,
    cultureFormatter,
    config
}) => {
    const {
        trackingLabel,
        salesLineQuantityText,
        shippingInformationHeadingText,
        noShippingInformationFoundDefaultText,
        containerIdLabelText,
        WeightLabelText,
        shippingCarrierLabelText,
        trackThisPackageLinkText
    } = resources;

    const { trackingUrls, imageSettings, noShipmentInformationMessage, showNoShipmentInformation } = config;

    const remainingSalesLines = [...salesLines];
    const noShipmentTrackingNodeProps: INodeProps = { tag: 'div', className: 'ms-order-details__shipment-tracking' };
    const noShipmentTrackingResources: INoShipmentTrackingResources = {
        noShippingFoundText: noShipmentInformationMessage || noShippingInformationFoundDefaultText
    };
    const noShipmentTrackingProps: INoShipmentTrackingProps = {
        noShipmentTrackingNodeProps,
        noShipmentTrackingResources
    };
    return (
        <>
            {containerDetails &&
                containerDetails.map(({ ShippingCarrier, ContainerId, ContainerLinesList, TrackingNumber, Weight }) => {
                    const foundItem = ContainerLinesList?.some(c =>
                        salesLines.find(sl => sl.data.salesLine.ItemId === c.ItemId && sl.data.salesLine.Quantity === c.Quantity)
                    );
                    if (!foundItem) {
                        return;
                    }
                    const shipmentTrackingNodeProps: INodeProps = { tag: 'div', className: 'ms-order-details__shipment-tracking' };
                    const shipmentTrackingResources: IShipmentTrackingResources = {
                        headingText: shippingInformationHeadingText,
                        containerIdLabel: containerIdLabelText,
                        weightLabel: WeightLabelText,
                        shippingCarrierLabel: shippingCarrierLabelText,
                        trackingLabel: trackingLabel,
                        trackthisPackageLinkText: trackThisPackageLinkText
                    };

                    const trackingUrlData = trackingUrls?.find(url => url.shippingCarrier === ShippingCarrier);
                    const shipmentTrackingProps: IShipmentTrackingProps = {
                        shipmentTrackingNodeProps,
                        shipmentTrackingResources,
                        containerId: ContainerId,
                        weight: Weight,
                        shippingCarrier: ShippingCarrier,
                        trackingNumber: TrackingNumber,
                        trackingUrl: trackingUrlData ? `${trackingUrlData.trackingUrl || ''}${TrackingNumber || ''}` : undefined
                    };

                    return (
                        <div key={ContainerId} className='ms-order-details__sales-line--item-container'>
                            <div className='ms-order-details__sales-line--item-container-lines'>
                                {ContainerLinesList &&
                                    ContainerLinesList.map(cl => {
                                        const itemData = salesLines.find(sl => sl.data.product?.ItemId === cl.ItemId);
                                        if (!itemData) {
                                            return <></>;
                                        }
                                        const index = remainingSalesLines.findIndex(
                                            sl => sl.data.product?.ItemId === cl.ItemId && sl.data.salesLine.Quantity === cl.Quantity
                                        );
                                        remainingSalesLines.splice(index, 1);
                                        const product = itemData.data.product;
                                        const imageProps: IOrderDetailsContainerImageProps = {
                                            product: product,
                                            actionContext: actionContext,
                                            requestContext: request,
                                            imageSettings: imageSettings
                                        };
                                        const netAmount = itemData?.data.salesLine.NetAmount || 0;
                                        const grossAmount = itemData?.data.salesLine.GrossAmount || 0;
                                        const orderDetailsContainerRowProps: IOrderDetailsContainerRowProps = {
                                            name: itemData?.data.product?.Name,
                                            itemId: cl.ItemId,
                                            imageUrl: itemData?.data.product?.PrimaryImageUrl,
                                            productUrl: itemData?.data.productUrl,
                                            quantity: cl.Quantity,
                                            netAmount: netAmount,
                                            strikeAmount: grossAmount > netAmount ? grossAmount : 0,
                                            salesStatus: itemData?.data.salesStatus,
                                            quantityLabel: salesLineQuantityText,
                                            buyAgainButton: itemData.buyAgainButton,
                                            renderedImage: <RenderImageComponent {...imageProps} />,
                                            cultureFormatter: cultureFormatter
                                        };
                                        return <OrderDetailsContainerRowComponent {...orderDetailsContainerRowProps} />;
                                    })}
                            </div>
                            <ShipmentTrackingComponent {...shipmentTrackingProps} />
                        </div>
                    );
                })}
            {remainingSalesLines.length > 0 && (
                <div className='ms-order-details__sales-line--item-container'>
                    <div className='ms-order-details__sales-line--item-container-lines'>
                        {remainingSalesLines.map(sl => {
                            const product = sl.data.product;
                            const imageProps: IOrderDetailsContainerImageProps = {
                                product: product,
                                actionContext: actionContext,
                                requestContext: request,
                                imageSettings: imageSettings
                            };
                            const netAmount = sl?.data.salesLine.NetAmount || 0;
                            const grossAmount = sl?.data.salesLine.GrossAmount || 0;
                            const orderDetailsContainerRowProps: IOrderDetailsContainerRowProps = {
                                name: sl?.data.product?.Name,
                                itemId: sl?.data.salesLine?.ItemId,
                                imageUrl: sl?.data.product?.PrimaryImageUrl,
                                productUrl: sl?.data.productUrl,
                                quantity: sl?.data.salesLine.Quantity,
                                netAmount: netAmount,
                                strikeAmount: grossAmount > netAmount ? grossAmount : 0,
                                salesStatus: sl?.data.salesStatus,
                                quantityLabel: salesLineQuantityText,
                                buyAgainButton: sl.buyAgainButton,
                                renderedImage: <RenderImageComponent {...imageProps} />,
                                cultureFormatter: cultureFormatter
                            };
                            return <OrderDetailsContainerRowComponent {...orderDetailsContainerRowProps} />;
                        })}
                    </div>
                    {showNoShipmentInformation && <NoShipmentTrackingComponent {...noShipmentTrackingProps} />}
                </div>
            )}
        </>
    );
};

const RenderImageComponent: React.FC<IOrderDetailsContainerImageProps> = (props: IOrderDetailsContainerImageProps) => {
    const product = props.product;
    if (!product) {
        return <></>;
    }
    const fallbackImageUrl = getFallbackImageUrl(
        product.ItemId,
        props.actionContext.requestContext.apiSettings,
        props.requestContext.app?.config?.OmniChannelMedia
    );

    const containerClassName = `props__product-image-container`;

    const productName = product.Name ?? product.Description;

    return (
        <div className={containerClassName}>
            <Image
                requestContext={props.actionContext.requestContext}
                className={`${containerClassName}__product-image`}
                altText={productName}
                // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- Need to set empty by default.
                src={product.PrimaryImageUrl || 'empty'}
                fallBackSrc={fallbackImageUrl}
                gridSettings={props.requestContext.gridSettings!}
                imageSettings={props.imageSettings}
                loadFailureBehavior='empty'
            />
        </div>
    );
};

export const GroupDelivery: React.FC<IGroupDelivery> = ({ deliveryProps, heading, count }) => (
    <Node {...deliveryProps}>
        {heading}
        {count}
    </Node>
);

export const Group: React.FC<IGroupExtended> = (props: IGroupExtended) => {
    const { groupProps, delivery, address } = props;
    return (
        <Node {...groupProps}>
            {delivery && <GroupDelivery {...delivery} />}
            {!delivery.showTimeslot && address}
            <Node className='ms-order-details__sales-lines_container'>
                <GroupedSalesLine {...props} />
                {delivery.showTimeslot && (
                    <Node {...delivery.pickupProps}>
                        {address}
                        {delivery.pickupDateTimeslot}
                    </Node>
                )}
            </Node>
        </Node>
    );
};

export const Groups: React.FC<IGroupsExtended> = (props: IGroupsExtended) => {
    const { groupsProps, groups } = props;
    return (
        <Node {...groupsProps}>
            {groups.map((group, index) => (
                <Group key={index} {...props} {...group} />
            ))}
        </Node>
    );
};

export const OrderSummary: React.FC<IOrderSummary> = ({
    orderSummaryProps,
    heading,
    subtotal,
    shipping,
    tax,
    totalAmount,
    earnedPoints
}) => (
    <Node {...orderSummaryProps}>
        {heading}
        {subtotal}
        {shipping}
        {tax}
        {totalAmount}
        {earnedPoints}
    </Node>
);

export const Payment: React.FC<IPaymentMethods> = ({ paymentMethodsProps, title, methods }) => (
    <Node {...paymentMethodsProps}>
        {title}
        {methods}
    </Node>
);

export const Help: React.FC<IHelp> = ({ helpProps, needHelpLabel, helpLineNumberLabel, contactNumber }) => (
    <Node {...helpProps}>
        {needHelpLabel}
        {helpLineNumberLabel}
        {contactNumber}
    </Node>
);

const OrderDetailsView: React.FC<IOrderDetailsViewProps & IOrderDetailsProps<IOrderDetailsExtData>> = props => {
    const {
        moduleProps,
        viewModes,
        tableViewActions,
        table,
        heading,
        alert,
        orderInfomation,
        orderSummary,
        payment,
        help,
        groups,
        config,
        data: { containerDetails },
        resources: { perCustomerFreightPolicyText, poNumberLabelText },
        context: { actionContext },
        salesOrder
    } = props;

    const [poNumber, setPoNumber] = React.useState<React.ReactNode>(null);
    const [isLoading, setIsLoading] = React.useState(true); // State to manage loading status

    React.useEffect(() => {
        if (!poNumber) {
            const salesId = salesOrder?.SalesId || '';
            void getPoNumber(salesId);
        }
    }, []);

    React.useEffect(() => {
        // 6849 - Replace the text FREE on the shipping line with Per Customer Freight Policy
        const shippingLine = document.querySelector<HTMLParagraphElement>(
            '.ms-order-details__order-summary-line-shipping .msc-price__actual'
        );
        if (shippingLine) {
            shippingLine.innerHTML = perCustomerFreightPolicyText;
        }
    }, []);

    const getPoNumber = async (salesId: string) => {
        setIsLoading(true);
        try {
            const salesDetails = await getSalesOrderDetailsAsync({ callerContext: actionContext }, [salesId]);
            if (salesDetails?.SalesOrderDetails?.length && salesDetails?.SalesOrderDetails[0].PoNumber) {
                const node = (
                    <span className='ms-order-details__order-information-sales-id'>
                        {poNumberLabelText}
                        {salesDetails?.SalesOrderDetails[0].PoNumber}
                    </span>
                );
                setPoNumber(node);
            }
        } catch (error) {
            console.error('Error fetching PO number:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const containerResults = containerDetails.result?.ContainerDetails || [];
    return (
        <Module {...moduleProps}>
            {isLoading || alert !== undefined ? (
                <div className='ms-order-details__spinner'>
                    <p className='ms-order-details__spinner__loading' />
                    <p className='ms-order-details__spinner__message'>{'Loading'}</p>
                </div>
            ) : (
                <>
                    {heading}
                    {orderInfomation && <OrderInfomation {...orderInfomation} purchaseOrderNumber={poNumber} />}
                    {tableViewActions}
                    {viewModes}
                    {table}
                    {groups && (
                        <Groups
                            {...props.context}
                            config={config}
                            containerDetails={containerResults}
                            resources={props.resources}
                            {...groups}
                        />
                    )}
                    <Node className='ms-order-details__order-summary-container'>
                        {orderSummary && <OrderSummary {...orderSummary} />}
                        {payment && <Payment {...payment} />}
                    </Node>
                    {help && <Help {...help} />}
                </>
            )}
        </Module>
    );
};

export default OrderDetailsView;
